import { useWindowSize } from "./useWindowSize";
import {
  isCompactLayout as isCompactLayoutBreakpoint,
  isMobile as isMobileBreakpoint,
  isTablet as isTabletBreakpoint
} from "@/assets/theme/sizes";

/**
 * Return type for useIsCompactLayout hook
 *
 * @property isCompactLayout - True for compact layouts (< 1024px) - mobile OR tablet
 * @property isMobile - True for mobile-only layouts (< 768px)
 * @property isTablet - True for tablet layouts (768px-1023px)
 * @property valueOf - Allows the object to be used as a boolean in conditionals
 * @property toString - String representation for debugging
 */
export interface UseIsCompactLayoutReturn {
  readonly isCompactLayout: boolean;
  readonly isMobile: boolean;
  readonly isTablet: boolean;
  valueOf(): boolean;
  toString(): string;
}

/**
 * Hook for detecting compact layouts (< 1024px), which includes both mobile and tablet sizes.
 *
 * This is the recommended hook to use instead of `useIsMobile()` with a clearer, more semantic name
 * that explicitly describes what it detects: compact layouts that need mobile/tablet-friendly UI.
 *
 * For actual device detection (iOS/Android), use `usePlatformDetection` instead.
 *
 * @returns A boolean-like object that can be used directly in conditionals, or destructured for explicit properties
 *
 * @example
 * // Use as boolean (< 1024px)
 * const isCompactLayout = useIsCompactLayout();
 * if (isCompactLayout) {
 *   return <MobileView />;
 * }
 *
 * @example
 * // Destructure for explicit layout detection
 * const { isCompactLayout, isMobile, isTablet } = useIsCompactLayout();
 * if (isTablet) {
 *   return <TabletOptimizedView />;
 * }
 *
 * @example
 * // Use in JSX conditionals
 * return useIsCompactLayout() ? <MobileNav /> : <DesktopNav />;
 */
export function useIsCompactLayout(): UseIsCompactLayoutReturn {
  const { width } = useWindowSize();

  const isCompactLayout = isCompactLayoutBreakpoint(width);
  const isMobile = isMobileBreakpoint(width);
  const isTablet = isTabletBreakpoint(width);

  return {
    isCompactLayout,
    isMobile,
    isTablet,
    valueOf: () => isCompactLayout,
    toString: () => String(isCompactLayout)
  };
}
